import { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { fractureAPI } from '../../services/api';

// ═══════════════════════════════════════════════
// 经济评价 — 共享参数(模块级单例, 切页保留)+ 纯计算函数
// 全部前端实时计算: 成本构成 / Arps 产量递减 / NPV·IRR·回收期
// ═══════════════════════════════════════════════
export type EcoParams = {
  // 成本单价
  fluidPrice: number;       // 元/m³
  proppantPrice: number;    // 元/t
  chemicalCost: number;     // 万元(直接给)
  equipmentCost: number;    // 万元
  laborCost: number;        // 万元
  otherCost: number;        // 万元
  // 产能 (Arps 指数递减)
  qi: number;               // 初始日产 万m³/d
  declineRate: number;      // 年递减率 %
  years: number;            // 评价年限
  // 财务
  price: number;            // 产品价格 元/m³
  opex: number;             // 年操作成本 万元/年
  discount: number;         // 贴现率 %
};

// 模块级单例(默认值对标一口页岩气水平井)
export const ECO_PARAMS: EcoParams = {
  fluidPrice: 8, proppantPrice: 1200,
  chemicalCost: 120, equipmentCost: 350, laborCost: 80, otherCost: 60,
  qi: 12, declineRate: 35, years: 15,
  price: 1.2, opex: 90, discount: 8,
};

// ── 从当前激活方案带出液砂量 ──
export function useActiveDesign(projectId?: number) {
  const [fluid, setFluid] = useState(0);
  const [prop, setProp] = useState(0);
  const [name, setName] = useState('');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (!projectId) { setLoading(false); return; }
    setLoading(true);
    fractureAPI.listDesigns(projectId)
      .then(({ data }) => {
        const ds: any[] = Array.isArray(data) ? data : (data?.results ?? []);
        const act = ds.find((d) => d.is_active) ?? ds[0];
        if (act) { setFluid(act.total_fluid || 0); setProp(act.total_proppant || 0); setName(act.name || ''); }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [projectId]);
  return { fluid, prop, name, loading };
}

// ── 成本构成 ──  单位统一万元
export function computeCost(p: EcoParams, fluidM3: number, propT: number) {
  const items = [
    { name: '压裂液', val: (fluidM3 * p.fluidPrice) / 1e4 },
    { name: '支撑剂', val: (propT * p.proppantPrice) / 1e4 },
    { name: '化学剂', val: p.chemicalCost },
    { name: '设备',   val: p.equipmentCost },
    { name: '人工',   val: p.laborCost },
    { name: '其他',   val: p.otherCost },
  ];
  const total = items.reduce((s, i) => s + i.val, 0);
  return { items, total };
}

// ── Arps 指数递减产量 ──  qi 万m³/d → 年产 万m³/年
export function computeProduction(p: EcoParams) {
  const D = p.declineRate / 100;
  const annual: { year: number; rate: number; cum: number }[] = [];
  let cum = 0;
  for (let t = 1; t <= p.years; t++) {
    const yr = p.qi * 365 * Math.exp(-D * (t - 0.5));   // 年中近似
    cum += yr;
    annual.push({ year: t, rate: yr, cum });
  }
  return { annual, eur: cum };                          // eur 万m³
}

// ── NPV / IRR / 回收期 ──  capex 万元; 年收入(万元)= 年产(万m³)×价格(元/m³)
export function computeNPV(p: EcoParams, capex: number, annual: { year: number; rate: number }[]): {
  npv: number;
  irr: number | null;
  payback: number | null;
  cumRows: { year: number; cf: number; cum: number }[];
} {
  const i = p.discount / 100;
  const cfs = annual.map((a) => a.rate * p.price - p.opex);   // 各年净现金流 万元

  const npvAt = (r: number) => -capex + cfs.reduce((s, cf, k) => s + cf / Math.pow(1 + r, k + 1), 0);
  const npv = npvAt(i);

  // IRR: 二分 0~200%
  let irr: number | null = null;
  let lo = 0, hi = 2;
  if (npvAt(lo) > 0 && npvAt(hi) < 0) {
    for (let k = 0; k < 60; k++) {
      const mid = (lo + hi) / 2;
      if (npvAt(mid) > 0) lo = mid; else hi = mid;
    }
    irr = ((lo + hi) / 2) * 100;
  }

  // 回收期(名义累计现金流转正, 线性插值)
  let cum = -capex; let payback: number | null = null;
  const cumRows = cfs.map((cf, k) => {
    const prev = cum; cum += cf;
    if (payback == null && prev < 0 && cum >= 0) {
      payback = k + (cf !== 0 ? -prev / cf : 0);
    }
    return { year: k + 1, cf, cum };
  });

  return { npv, irr, payback, cumRows };
}

export const ETH = {
  bg: '#FFFFFF', canvasBg: '#FFFFFF', grid: 'rgba(30,42,58,0.07)',
  text: '#1E2A3A', textSec: '#4F6680', textMuted: '#8598AD',
  accent: '#C2533A', accentLight: 'rgba(194,83,58,0.08)',
  ok: '#1E8449', warn: '#D4770C', stage: '#2874A6',
  headerBorder: '#D6DAE0', panelHeaderBg: '#F4F6F9',
  fontUI: '"Microsoft YaHei","PingFang SC","Hiragino Sans GB","Noto Sans SC",sans-serif',
  fontMono: '"Cascadia Code",Consolas,"SF Mono","Microsoft YaHei",monospace',
};

// 小输入框样式复用
export const ecoInputStyle: CSSProperties = {
  width: 70, fontSize: 12, padding: '3px 6px', borderRadius: 3,
  border: '1px solid #D6DAE0',
};
